/**
 * Renders a `template_change` view: the template tree with added, removed
 * and changed nodes marked, unchanged subtrees collapsed, per-field
 * "before → after" and question changes, counts, and a button that confirms
 * the change with one click.
 */
import { Box, Button, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import type { AgentViewComponentProps } from "../registry.js";
import { TemplateViewFrame } from "./TemplateViewFrame.js";
import {
  TEMPLATE_NODE_ICON,
  useChangeFieldLabel,
  useChangeQuestionValueFormatter,
  useFieldValueFormatter,
  useNodeDetailFormatter,
  useNodeDetailListFormatter,
  useTemplateNodeKindLabel,
} from "./templateNodeDisplay.js";
import {
  templateChangeViewSchema,
  type ChangeNode,
  type ChangeQuestionDefaultValue,
  type ChangeQuestionKind,
  type TemplateChangeView,
} from "./templateViewTypes.js";

/** A question's default value as plain text: a list joined by ", ", an unset value empty. */
function formatQuestionDefaultValue(
  value: ChangeQuestionDefaultValue | null
): string {
  if (value === null) return "";
  if (Array.isArray(value)) return value.map(String).join(", ");
  return String(value);
}

function QuestionFieldChange({
  label,
  before,
  after,
}: {
  label: string;
  before: string;
  after: string;
}) {
  return (
    <>
      <br />
      <span>
        {label}: <s>{before}</s> → <b>{after}</b>
      </span>
    </>
  );
}

const STATUS_COLOR: Record<ChangeNode["status"], string> = {
  unchanged: "transparent",
  added: "success.main",
  removed: "error.main",
  changed: "warning.main",
};

function ChangeTree({ nodes, depth }: { nodes: ChangeNode[]; depth: number }) {
  const { t } = useTranslate(["Assistant", "Global"]);
  const kindLabel = useTemplateNodeKindLabel();
  const formatDetailList = useNodeDetailListFormatter();
  const formatDetail = useNodeDetailFormatter();
  const formatFieldValue = useFieldValueFormatter();
  const fieldLabel = useChangeFieldLabel();
  const formatQuestionValue = useChangeQuestionValueFormatter();
  const questionKindLabel = (kind: ChangeQuestionKind): string => {
    switch (kind) {
      case "text":
        return t("Text", { ns: "Assistant" });
      case "select":
        return t("Single choice", { ns: "Assistant" });
      case "number":
        return t("Number", { ns: "Assistant" });
      case "select_number":
        return t("Single number choice", { ns: "Assistant" });
      case "multi_select":
        return t("Multiple choice", { ns: "Assistant" });
      case "multi_select_number":
        return t("Multiple number choice", { ns: "Assistant" });
      case "bool":
        return t("Yes/no", { ns: "Assistant" });
    }
  };
  const statusLabel = (status: ChangeNode["status"]): string | null => {
    switch (status) {
      case "added":
        return t("New", { ns: "Assistant" });
      case "removed":
        return t("Removed", { ns: "Assistant" });
      case "changed":
        return t("Changed", { ns: "Global" });
      case "unchanged":
        return null;
    }
  };
  // Unchanged parts stay visible for orientation but fold into one line.
  const shown = nodes.filter(n => n.changedBelow || depth === 0);
  const folded = nodes.length - shown.length;
  return (
    <Stack spacing={0.5}>
      {shown.map((node, index) => {
        const nodeStatusLabel = statusLabel(node.status);
        return (
          <Box
            key={node.itemId ?? `new-${index}`}
            sx={{ pl: depth === 0 ? 0 : 2 }}
          >
            <Box
              sx={{
                borderLeft: 3,
                borderColor: STATUS_COLOR[node.status],
                pl: 1,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: "flex-start" }}
              >
                <Tooltip title={kindLabel(node.kind)}>
                  <Box sx={{ pt: 0.25 }}>{TEMPLATE_NODE_ICON[node.kind]}</Box>
                </Tooltip>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{
                      fontWeight: node.children.length > 0 ? 600 : 400,
                      textDecoration:
                        node.status === "removed" ? "line-through" : undefined,
                      color:
                        node.status === "unchanged"
                          ? "text.secondary"
                          : undefined,
                    }}
                  >
                    {node.title || kindLabel(node.kind)}
                    {nodeStatusLabel && (
                      <Chip
                        size="small"
                        label={nodeStatusLabel}
                        sx={{
                          ml: 1,
                          height: 18,
                          bgcolor: STATUS_COLOR[node.status],
                          color: "common.white",
                        }}
                      />
                    )}
                  </Typography>
                  {node.detail &&
                    node.detail.length > 0 &&
                    node.fieldChanges.length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {formatDetailList(node.detail)}
                      </Typography>
                    )}
                  {node.fieldChanges.map(change => (
                    <Typography
                      key={change.field}
                      variant="caption"
                      component="div"
                    >
                      {fieldLabel(change.field)}:{" "}
                      <s>{formatFieldValue(change.before)}</s> →{" "}
                      <b>{formatFieldValue(change.after)}</b>
                    </Typography>
                  ))}
                  {node.questionChanges.map(change => (
                    <Typography
                      key={change.key}
                      variant="caption"
                      component="div"
                    >
                      {change.status === "added" &&
                        t("Question added: {value}", {
                          ns: "Assistant",
                          value:
                            formatQuestionValue(change.after) || change.key,
                        })}
                      {change.status === "removed" &&
                        t("Question removed: {value}", {
                          ns: "Assistant",
                          value:
                            formatQuestionValue(change.before) || change.key,
                        })}
                      {change.status === "changed" && (
                        <>
                          {t("Question changed:", { ns: "Assistant" })}{" "}
                          <s>{formatQuestionValue(change.before)}</s> →{" "}
                          <b>{formatQuestionValue(change.after)}</b>
                        </>
                      )}
                      {change.kind && (
                        <QuestionFieldChange
                          label={t("Question type", { ns: "Assistant" })}
                          before={
                            change.kind.before
                              ? questionKindLabel(change.kind.before)
                              : ""
                          }
                          after={questionKindLabel(change.kind.after)}
                        />
                      )}
                      {change.options && (
                        <>
                          <br />
                          <s>
                            {change.options.before
                              ? formatDetail(change.options.before)
                              : ""}
                          </s>{" "}
                          →{" "}
                          <b>
                            {change.options.after
                              ? formatDetail(change.options.after)
                              : ""}
                          </b>
                        </>
                      )}
                      {change.unit && (
                        <QuestionFieldChange
                          label={t("Unit", { ns: "Assistant" })}
                          before={change.unit.before ?? ""}
                          after={change.unit.after ?? ""}
                        />
                      )}
                      {change.value && (
                        <QuestionFieldChange
                          label={t("Default value", { ns: "Assistant" })}
                          before={formatQuestionDefaultValue(
                            change.value.before
                          )}
                          after={formatQuestionDefaultValue(change.value.after)}
                        />
                      )}
                      {change.journeys && (
                        <QuestionFieldChange
                          label={t("Journeys", { ns: "Assistant" })}
                          before={change.journeys.before.join(", ")}
                          after={change.journeys.after.join(", ")}
                        />
                      )}
                    </Typography>
                  ))}
                </Box>
              </Stack>
            </Box>
            {node.children.length > 0 && (node.changedBelow || depth === 0) && (
              <Box sx={{ mt: 0.5 }}>
                <ChangeTree nodes={node.children} depth={depth + 1} />
              </Box>
            )}
          </Box>
        );
      })}
      {folded > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ pl: depth === 0 ? 0 : 2 }}
        >
          {t("{count} unchanged entries", { ns: "Assistant", count: folded })}
        </Typography>
      )}
    </Stack>
  );
}

function ChangeViewInner({
  view,
  onSend,
  busy,
  answeredWith,
}: {
  view: TemplateChangeView;
  onSend: AgentViewComponentProps["onSend"];
  busy: boolean;
  answeredWith: AgentViewComponentProps["answeredWith"];
}) {
  const { t } = useTranslate(["Assistant"]);
  const { added, removed, changed } = view.summary;

  const confirm = () => {
    onSend(t("Yes, apply the change exactly as shown.", { ns: "Assistant" }), {
      confirm: true,
    });
  };

  return (
    <TemplateViewFrame
      title={view.title}
      description={view.description}
      renderBody={() => (
        <Stack spacing={1}>
          <Stack direction="row" spacing={0.5}>
            {added > 0 && (
              <Chip
                size="small"
                color="success"
                label={t("{count} new", { ns: "Assistant", count: added })}
              />
            )}
            {changed > 0 && (
              <Chip
                size="small"
                color="warning"
                label={t("{count} changed", {
                  ns: "Assistant",
                  count: changed,
                })}
              />
            )}
            {removed > 0 && (
              <Chip
                size="small"
                color="error"
                label={t("{count} removed", {
                  ns: "Assistant",
                  count: removed,
                })}
              />
            )}
          </Stack>
          <ChangeTree nodes={view.tree} depth={0} />
          <Box>
            <Button
              variant="contained"
              size="small"
              onClick={confirm}
              disabled={busy || answeredWith !== undefined}
            >
              {t("Apply change", { ns: "Assistant" })}
            </Button>
          </Box>
        </Stack>
      )}
    />
  );
}

export function ChangeView({
  view,
  onSend,
  busy,
  answeredWith,
}: AgentViewComponentProps) {
  const parsed = templateChangeViewSchema.safeParse(view);
  if (!parsed.success) return null;
  return (
    <ChangeViewInner
      view={parsed.data}
      onSend={onSend}
      busy={busy}
      answeredWith={answeredWith}
    />
  );
}
